<!-- 試用版申込フォーム STEP3：入力内容を一覧表示し、最終確認・送信を行う画面 -->
<template>
  <v-container fluid class="pa-0">
    <PageHeader />

    <div class="content-container">
      <StepNavigator :current="3" />

      <!-- ===============================
         ご利用になるお客様情報
    =============================== -->
      <ConfirmSection title="ご利用になるお客様情報">
        <CompanyInfoTable :data="f" />
      </ConfirmSection>

      <!-- ===============================
         管理者情報
    =============================== -->
      <ConfirmSection v-if="hasManager" title="管理者情報（任意）">
        <CompanyInfoTable :data="f.manager" company-label="法人名" />
      </ConfirmSection>

      <!-- ===============================
         販売代理店情報
    =============================== -->
      <ConfirmSection v-if="hasAgency" title="販売代理店情報（任意）">
        <CompanyInfoTable
          :data="f.agency"
          company-label="法人名"
          :show-mail-delivery="false"
        />
      </ConfirmSection>

      <!-- ===============================
         アカウント情報の送付先
    =============================== -->
      <ConfirmSection title="アカウント情報の送付先">
        <template v-for="(target, i) in deliveryTargets" :key="target.type">
          <ConfirmRow
            :label="deliveryRowLabel(i)"
            :value="target.label"
            :group-start="i > 0"
          />
          <ConfirmRow label="お名前" :value="target.name" />
          <ConfirmRow
            label="メールアドレス"
            :value="target.email"
            :group-end="i < deliveryTargets.length - 1"
          />
        </template>
      </ConfirmSection>

      <!-- ===============================
         ご依頼内容
    =============================== -->
      <ConfirmSection title="ご依頼内容">
        <ConfirmRow label="顧客 ID" :value="f.customerId" />
        <ConfirmRow label="ご選択オプション" :value="selectedOptions" />
        <template v-if="f.options.fingerprint">
          <ConfirmRow
            label="指紋認証ユニットの貸出"
            :value="`${f.rental.quantity} 台`"
          />
          <ConfirmRow label="貸出送付先" :value="rentalTarget" />
        </template>
        <ConfirmRow
          label="利用規約への承諾"
          :value="f.agreeTerms ? '承諾する' : '—'"
        />
        <ConfirmRow
          label="個人情報保護方針への同意"
          :value="f.agreePrivacy ? '承諾する' : '—'"
        />
      </ConfirmSection>

      <!-- ボタン -->
      <v-alert v-if="submitError" type="error" variant="outlined" class="mb-6">
        {{ submitError }}
      </v-alert>

      <div class="button-area two-buttons">
        <v-btn
          class="button-secondary"
          @click="goBack"
          :disabled="isSubmitting"
          size="large"
          variant="outlined"
        >
          戻る
        </v-btn>

        <v-btn
          class="button-primary"
          @click="submit"
          :disabled="isSubmitting"
          :loading="isSubmitting"
          size="large"
        >
          送信
        </v-btn>
      </div>
    </div>
  </v-container>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import {
  useExperienceFormStore,
  type DeliveryType,
} from "../store/useExperienceFormStore";
import { markStepCompleted } from "../router/index";
import PageHeader from "../components/layout/PageHeader.vue";
import StepNavigator from "../components/StepNavigator.vue";
import ConfirmSection from "../components/confirm/ConfirmSection.vue";
import ConfirmRow from "../components/confirm/ConfirmRow.vue";
import CompanyInfoTable from "../components/confirm/CompanyInfoTable.vue";

const isSubmitting = ref(false);

const router = useRouter();
const store = useExperienceFormStore();
const f = computed(() => store.form);

const hasManager = computed(() =>
  Object.values(store.form.manager).some((v) => v),
);
const hasAgency = computed(() =>
  Object.values(store.form.agency).some((v) => v),
);

const optionLabels = {
  windows: "Windowsログオン認証オプション",
  idManager: "IDマネージャーオプション",
  fingerprint: "指紋認証オプション",
  iccard: "ICカードオプション",
};

const selectedOptions = computed(() =>
  Object.entries(f.value.options || {})
    .filter(([_, v]) => v)
    .map(([k]) => optionLabels[k as keyof typeof optionLabels])
    .join("、"),
);

const rentalTarget = computed(() => {
  switch (f.value.rental?.receiver) {
    case "customer":
      return "ご利用になるお客様";
    case "manager":
      return "管理者様";
    case "agency":
      return "販売代理店";
    default:
      return "";
  }
});

// 送付先の表示順は入力画面の並びに合わせる（選択順に左右されないようにする）
const deliveryOrder: DeliveryType[] = ["customer", "manager", "agency", "other"];
const deliveryLabels: Record<DeliveryType, string> = {
  customer: "ご利用になるお客様",
  manager: "管理者様",
  agency: "販売代理店",
  other: "その他",
};

// 送付先ごとの氏名・メールアドレス（メール本文と同じ引き当て方をする）
const deliveryTargets = computed(() =>
  deliveryOrder
    .filter((type) => f.value.delivery?.types?.includes(type))
    .map((type) => {
      const label = deliveryLabels[type];

      switch (type) {
        case "manager":
          return {
            type,
            label,
            name: f.value.manager?.name,
            email: f.value.manager?.email,
          };
        case "agency":
          return {
            type,
            label,
            name: f.value.agency?.name,
            email: f.value.agency?.email,
          };
        case "other":
          return {
            type,
            label,
            name: f.value.delivery?.name,
            email: f.value.delivery?.email,
          };
        default:
          return { type, label, name: f.value.name, email: f.value.email };
      }
    }),
);

// 複数選択時のみ連番を付ける（メール本文と揃える）
const deliveryIndexLabels = ["１", "２", "３", "４"];

function deliveryRowLabel(index: number): string {
  if (deliveryTargets.value.length <= 1) {
    return "送付先";
  }
  return `送付先${deliveryIndexLabels[index] ?? index + 1}`;
}

function goBack() {
  router.push("/step2");
}

function getSubmitErrorMessage(resultCode: number | null): string {
  switch (resultCode) {
    case -200:
      return "この顧客IDは既に使用されています。別のIDを入力してください。";
    case -100:
      return "入力内容に不備があります。内容をご確認ください。";
    case -300:
      return "サーバーエラーが発生しました。時間をおいて再度お試しください。";
    default:
      return "申し訳ありません。お申し込みの処理に失敗しました。";
  }
}

const submitError = ref<string | null>(null);

async function submit() {
  if (isSubmitting.value) return;

  submitError.value = null;
  isSubmitting.value = true;

  try {
    const res = await store.submit();

    if (!res) {
      submitError.value = "送信に失敗しました。もう一度お試しください。";
      return;
    }

    const resultCode = res.resultCode ?? null;

    if (resultCode !== 0) {
      // サーバーからのメッセージを優先、なければローカルメッセージ
      submitError.value = res.message || getSubmitErrorMessage(resultCode);
      return;
    }

    // 成功
    markStepCompleted("step3");
    router.push("/step4");
    // フォームリセットとフラグクリアはstep4で実行される
  } catch (e) {
    console.error("申し込み送信エラー:", e);
    submitError.value =
      "通信エラーが発生しました。ネットワークをご確認ください。";
  } finally {
    isSubmitting.value = false;
  }
}
</script>
